import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "2026-07", title: "July 2026" },
  { id: "2026-06", title: "June 2026" },
  { id: "earlier", title: "Earlier milestones" },
  { id: "subscribe", title: "Follow along" },
];

type Entry = {
  date: string;
  tag: "Shipped" | "Improved" | "Fixed" | "Security";
  title: string;
  body: string;
};

const TAG_STYLES: Record<Entry["tag"], string> = {
  Shipped: "bg-primary/15 text-primary",
  Improved: "bg-sky-500/15 text-sky-400",
  Fixed: "bg-amber-500/15 text-amber-400",
  Security: "bg-rose-500/15 text-rose-400",
};

const july: Entry[] = [
  { date: "Jul 3, 2026", tag: "Shipped", title: "Public roadmap & changelog", body: "Added /roadmap and /changelog so partners can see what's in flight, next, and recently shipped." },
  { date: "Jul 2, 2026", tag: "Shipped", title: "Trust Center & Accessibility Statement", body: "New /trust hub and /accessibility statement covering WCAG 2.1 AA targets, subprocessors, and privacy handling." },
  { date: "Jul 2, 2026", tag: "Shipped", title: "Quarterly intelligence reports v0", body: "AI-drafted quarterly reports gated by email capture, delivered as signed-URL PDFs; admin dashboard for reports & captured leads." },
  { date: "Jul 2, 2026", tag: "Shipped", title: "/for-funders and /for-agencies landings", body: "Consent-gated demo request forms feeding partner applications." },
  { date: "Jul 1, 2026", tag: "Improved", title: "Research consent controls", body: "Opt-in checkbox on sign-up plus a Privacy tab in Account Settings; Privacy Policy and Terms updated with explicit revocation clauses." },
  { date: "Jul 1, 2026", tag: "Improved", title: "Pro gating expanded", body: "CSV export in Grant Finder, funder 990 deep views, and legislation alerts are now gated to Pro members." },
  { date: "Jul 1, 2026", tag: "Security", title: "Redirect allowlist & LinkedIn PII minimization", body: "Email tracker now enforces a trusted-host allowlist; LinkedIn linking persists only minimal profile fields." },
];

const june: Entry[] = [
  { date: "Jun 30, 2026", tag: "Shipped", title: "Structured logging across cron jobs", body: "All scheduled functions emit invoked/complete JSON with a shared request_id." },
  { date: "Jun 30, 2026", tag: "Shipped", title: "Cron heartbeats & Admin Pipelines strip", body: "BetterStack heartbeats wired into 8 background jobs; Admin dashboard surfaces LegiScan, Congress.gov, and email queue health." },
  { date: "Jun 29, 2026", tag: "Shipped", title: "Legal data ingestion pipeline", body: "Daily Congress.gov ingest of Alabama delegation bills with revision history, focus-area tagging, and stale-source monitoring." },
  { date: "Jun 28, 2026", tag: "Shipped", title: "Automated match routing", body: "Idea match requests auto-route to scored partners with in-app notifications; partners can Claim or Pass from the Leads tab." },
  { date: "Jun 28, 2026", tag: "Shipped", title: "Partner referral plumbing", body: "New /r/:code redirect, 90-day attribution cookie, and click metrics on the Affiliate Dashboard." },
  { date: "Jun 27, 2026", tag: "Shipped", title: "Analytics funnels & attribution", body: "First-touch UTM/referrer capture, $set_once identify persistence, and PostHog funnel/retention provisioning docs." },
  { date: "Jun 26, 2026", tag: "Shipped", title: "Weekly grants digest & in-app notifications", body: "Tuesday-morning personalized digest, daily deadline & tracked-bill alerts, with open/click tracking." },
  { date: "Jun 25, 2026", tag: "Shipped", title: "Monetization rails", body: "Plan entitlements, sponsor tiers, boost scheduling with concurrency caps, and an Admin boost moderation queue backed by Paddle refunds." },
  { date: "Jun 24, 2026", tag: "Shipped", title: "Paddle payments (sandbox)", body: "End-to-end checkout, subscription lifecycle, boost purchases, and webhook handling." },
];

const EntryList = ({ entries }: { entries: Entry[] }) => (
  <ul className="not-prose space-y-4">
    {entries.map((e) => (
      <li key={e.title + e.date} className="p-4 rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${TAG_STYLES[e.tag]}`}>{e.tag}</span>
          <span className="text-xs text-muted-foreground">{e.date}</span>
        </div>
        <p className="font-display font-semibold text-foreground mb-1">{e.title}</p>
        <p className="text-sm text-muted-foreground leading-relaxed">{e.body}</p>
      </li>
    ))}
  </ul>
);

const Changelog = () => (
  <>
    <Helmet>
      <title>Changelog — The Emerging Market</title>
      <meta
        name="description"
        content="Recently shipped changes on The Emerging Market — features, improvements, fixes, and security updates."
      />
      <link rel="canonical" href="https://emrkt.co/changelog" />
      <meta property="og:title" content="Changelog — The Emerging Market" />
      <meta property="og:url" content="https://emrkt.co/changelog" />
      <meta property="og:type" content="website" />
    </Helmet>
    <LegalLayout
      title="Changelog"
      description="A running log of what we've shipped, improved, fixed, or hardened. For what's coming, see the Roadmap."
      lastUpdated="July 3, 2026"
      sections={sections}
    >
      <section id="2026-07">
        <h2>July 2026</h2>
        <EntryList entries={july} />
      </section>

      <section id="2026-06">
        <h2>June 2026</h2>
        <EntryList entries={june} />
      </section>

      <section id="earlier">
        <h2>Earlier milestones</h2>
        <p>
          Foundational work — Alabama-first data model, the Archive, Ideas
          Board, Grant Finder, Legislation Tracker, Community News, Partner
          Network, Coach, and the initial Admin surface — landed across
          spring 2026. We didn't keep a public log at the time; future
          entries will always appear here first.
        </p>
      </section>

      <section id="subscribe">
        <h2>Follow along</h2>
        <p>
          For what's next, see the <a href="/roadmap">Product Roadmap</a>.
          Uptime and incident history live on the{" "}
          <a href="/status">Status</a> page. Email{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a> to suggest an
          addition.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default Changelog;